import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

// Translations object
export const translations = {
  en: {
    // Navigation
    nav: {
      home: "Home",
      about: "About",
      specialities: "Specialities",
      facilities: "Facilities",
      healthPackages: "Health Packages",
      testimonials: "Testimonials",
      blog: "Blog",
      contact: "Contact",
      bookAppointment: "Book Now",
    },
    // Hero Section
    hero: {
      tagline: "Your Trust is Our Sakthi",
      subtitle: "Best Hospital in Triplicane, Royapettah & Mylapore",
      description: "Trusted by 1,50,000+ families for compassionate, patient-centric healthcare close to home.",
      cta: "Book Appointment",
      viewDoctors: "Meet Our Doctors",
      emergency: "24/7 Emergency",
      emergencyNumber: "+91 44 2846 1515",
    },
    // About Section
    about: {
      title: "About Sakthi Hospital",
      subtitle: "Where Healing Begins with The Heart",
      tagline: "Our Happy Journey",
      description: "Sakthi Hospital & Research Centre (SHRC) started in 1985 as a 20-bed nursing home in Mannady, Chennai. It evolved into a 75-bedded multi-specialty hospital in Triplicane in 2000. NABH accredited since 2017. Trusted by 1,50,000+ families for compassionate, patient-centric healthcare with 24x7 Emergency, ICU, and advanced diagnostics.",
      stats: {
        beds: "75+ Beds",
        experience: "40+ Years",
        specialists: "50+ Doctors",
        patients: "1.5L+ Families",
      },
    },
    // Specialities Section
    specialities: {
      title: "Our Specialities",
      subtitle: "Comprehensive Care Across Multiple Disciplines",
      departments: {
        obgyn: {
          name: "Obstetrics & Gynaecology",
          desc: "40+ years legacy. Normal & high-risk pregnancy, infertility (IUI/IVF), laparoscopic surgery, CAPSI cancer screening, and aesthetic gynaecology.",
        },
        cardio: {
          name: "Cardiology",
          desc: "Heart attack treatment, cardiac failure, arrhythmia, ECG, ECHO, TMT, and non-invasive cardiac procedures.",
        },
        urology: {
          name: "Urology",
          desc: "Laser stone removal, laser prostate surgery, Mini PCNL, robotic surgery, kidney transplant, and reconstructive urology.",
        },
        ortho: {
          name: "Orthopaedics",
          desc: "Joint replacements, arthroscopy, spinal surgery, trauma fracture surgeries, sports injuries, and minimally invasive keyhole surgeries.",
        },
        gastro: {
          name: "Gastroenterology",
          desc: "Endoscopy, colonoscopy, laparoscopic surgeries (appendectomy, cholecystectomy, hernia repair), liver and pancreatic procedures.",
        },
        ent: {
          name: "ENT",
          desc: "Tonsillectomy, mastoidectomy, endoscopic sinus surgery (FESS), thyroid surgery, snoring treatment, and micro ear surgeries.",
        },
        paediatrics: {
          name: "Paediatrics & Neonatology",
          desc: "Fully equipped NICU with ventilator, neonatologist on call, immunization services, and comprehensive newborn care.",
        },
        medicine: {
          name: "General & Internal Medicine",
          desc: "Diabetes management, fever, infections, thyroid, health check-ups, pulmonology, rheumatology, and preventive medicine.",
        },
        spine: {
          name: "Spine & Neurology",
          desc: "Craniotomy, spondylosis, scoliosis, spine tumours, slipped disc treatment, posterior stabilization, and 24/7 spine emergencies.",
        },
        oncology: {
          name: "Oncology",
          desc: "CAPSI — Cancer Awareness, Prevention & Screening Initiative. Multidisciplinary approach with reconstructive surgery and rehabilitation.",
        },
        surgery: {
          name: "Laparoscopy & General Surgery",
          desc: "Minimally invasive keyhole surgeries, hernia repair, appendectomy, vascular surgery, and podiatric surgery.",
        },
        plastic: {
          name: "Plastic & Cosmetic Surgery",
          desc: "Cosmetic surgery (face, breast, body), burns management, hand injuries, diabetic foot care, and craniofacial anomalies.",
        },
        diabetology: {
          name: "Diabetology",
          desc: "Type I & II diabetes, gestational diabetes, obesity treatment, diabetic foot care, nutritional and exercise therapy.",
        },
        skin: {
          name: "Dermatology & Cosmetology",
          desc: "Complete dermatology services including cosmetic treatments, hair restoration, and skin disorders.",
        },
      },
      learnMore: "Learn More",
    },
    // Facilities Section
    facilities: {
      title: "World-Class Facilities",
      subtitle: "State-of-the-art Infrastructure for Your Care",
      items: {
        ot: {
          name: "State-of-Art Operation Theatres",
          desc: "3 fully equipped OTs with laminar air flow, latest technology and strict infection control protocols",
        },
        icu: {
          name: "24/7 ICU with Intensivist",
          desc: "Dedicated intensivist-managed ICU with round-the-clock monitoring, ventilator and cardiac support",
        },
        emergency: {
          name: "24/7 Emergency & Ambulance",
          desc: "Fully equipped emergency department with 24/7 ambulance services for immediate assistance",
        },
        nicu: {
          name: "NICU & Neonatology",
          desc: "Fully equipped NICU with ventilator, neonatologist on call, and comprehensive newborn care",
        },
        dialysis: {
          name: "Dialysis Unit",
          desc: "Modern dialysis center with ICU backup, handling both acute and chronic kidney disease patients",
        },
        lab: {
          name: "Advanced Diagnostics",
          desc: "Digital X-Ray, ECG, 2D ECHO, Ultrasound, Endoscopy, Colonoscopy, TMT, PFT, and full lab services",
        },
        pharmacy: {
          name: "24-Hour Pharmacy",
          desc: "Round-the-clock in-house pharmacy with all essential and specialty medicines",
        },
        homecare: {
          name: "Home Care Services",
          desc: "Post-discharge care, wound management, physiotherapy and nursing services at your doorstep",
        },
        insurance: {
          name: "Insurance & TPA Cashless",
          desc: "Cashless treatment with major insurance providers and TPA facilitation for hassle-free billing",
        },
        physio: {
          name: "Physiotherapy & Rehabilitation",
          desc: "Comprehensive physiotherapy services for post-surgical recovery, sports injuries, and chronic pain",
        },
      },
    },
    // Appointment Form
    appointment: {
      title: "Book Appointment",
      subtitle: "Schedule your visit with our specialists",
      form: {
        name: "Full Name",
        namePlaceholder: "Enter your name",
        phone: "Phone Number",
        phonePlaceholder: "+91 XXXXX XXXXX",
        email: "Email Address",
        emailPlaceholder: "your@email.com",
        date: "Preferred Date",
        time: "Preferred Time",
        department: "Select Department",
        message: "Additional Notes (Optional)",
        messagePlaceholder: "Any specific concerns or requirements...",
        submit: "Submit",
        submitting: "Submitting...",
      },
      success: "Appointment booked successfully! We will contact you shortly.",
      error: "Failed to book appointment. Please try again.",
      departments: [
        "Obstetrics & Gynaecology",
        "Urology",
        "Cardiology",
        "Orthopaedics",
        "Gastroenterology",
        "ENT",
        "Paediatrics & Neonatology",
        "General & Internal Medicine",
        "Spine & Neurology",
        "Oncology",
        "Laparoscopy & General Surgery",
        "Plastic & Cosmetic Surgery",
        "Diabetology",
        "Dermatology & Cosmetology",
        "Dialysis",
      ],
    },
    // Testimonials
    testimonials: {
      title: "What Our Patients Say",
      subtitle: "Real reviews from Google My Business",
    },
    // Blog
    blog: {
      title: "Latest Updates",
      subtitle: "Health tips and news from our experts",
      readMore: "Learn more",
    },
    // Contact
    contact: {
      title: "Get in Touch",
      subtitle: "We're here to help you",
      address: {
        title: "Address",
        line1: "Sakthi Hospital & Research Centre",
        line2: "175, Big Street, Triplicane",
        line3: "Chennai - 600005, Tamil Nadu",
      },
      phone: {
        title: "Phone",
        main: "+91 44 2844 2626",
        emergency: "+91 98402 64444",
      },
      email: {
        title: "Email",
        address: "info@sakthihospitals.com",
      },
      hours: {
        title: "Working Hours",
        opd: "OPD: 9:00 AM - 9:00 PM",
        emergency: "Emergency: 24/7",
      },
      form: {
        name: "Your Name",
        email: "Email Address",
        phone: "Phone Number",
        message: "Your Message",
        submit: "Send Message",
      },
    },
    // Footer
    footer: {
      tagline: "Your Trust is Our Sakthi",
      description: "Trusted multi-specialty hospital serving Triplicane, Royapettah & Mylapore for over 25 years.",
      quickLinks: "Quick Links",
      departments: "Departments",
      contact: "Contact Us",
      copyright: "© 2025 Sakthi Hospital & Research Centre. All rights reserved.",
    },
    // FAQ
    faq: {
      title: "Usually Asked",
      subtitle: "What People",
      questions: [
        {
          q: "What services does Sakthi Hospital has to offer?",
          a: "We offer comprehensive healthcare services including Gynaecology, Cardiology, Urology, General Medicine, Orthopaedics, Gastroenterology, and more with 24/7 emergency care."
        },
        {
          q: "How do I schedule an appointment with Sakthi Hospital?",
          a: "You can book an appointment online through our website, call us at +91 44 2846 1515, or visit our hospital directly during OPD hours (9 AM - 9 PM)."
        },
        {
          q: "Can you arrange transportation?",
          a: "Yes, we provide 24/7 ambulance services for emergencies. Call our emergency helpline for immediate assistance."
        },
        {
          q: "What should I bring to my appointment?",
          a: "Please bring your previous medical records, any current medications, valid ID proof, and insurance documents if applicable."
        },
        {
          q: "How do I request a prescription refill?",
          a: "You can request prescription refills by visiting our pharmacy or contacting us through phone during working hours."
        }
      ]
    }
  },
  ta: {
    // Navigation
    nav: {
      home: "முகப்பு",
      about: "எங்களைப் பற்றி",
      specialities: "சிறப்புகள்",
      facilities: "வசதிகள்",
      healthPackages: "சுகாதார பேக்கேஜ்கள்",
      testimonials: "சான்றுகள்",
      blog: "செய்திகள்",
      contact: "தொடர்பு",
      bookAppointment: "முன்பதிவு",
    },
    // Hero Section
    hero: {
      tagline: "உங்கள் நம்பிக்கையே எங்கள் சக்தி",
      subtitle: "திருவல்லிக்கேணி, ராயப்பேட்டை & மயிலாப்பூரில் சிறந்த மருத்துவமனை",
      description: "1,50,000+ குடும்பங்களால் நம்பப்படும் இரக்கமான, நோயாளி-மைய சுகாதார பராமரிப்பு.",
      cta: "சந்திப்பு முன்பதிவு",
      viewDoctors: "மருத்துவர்களை சந்திக்கவும்",
      emergency: "24/7 அவசர சேவை",
      emergencyNumber: "+91 44 2846 1515",
    },
    // About Section
    about: {
      title: "சக்தி மருத்துவமனை பற்றி",
      subtitle: "இதயத்துடன் குணமாகும் இடம்",
      tagline: "எங்கள் மகிழ்ச்சியான பயணம்",
      description: "சக்தி மருத்துவமனை & ஆராய்ச்சி மையம் (SHRC) 1985-ல் மண்ணடியில் 20 படுக்கைகள் கொண்ட நர்சிங் ஹோமாக தொடங்கப்பட்டது. 2000-ல் திருவல்லிக்கேணியில் 75 படுக்கைகள் கொண்ட பல்துறை சிறப்பு மருத்துவமனையாக வளர்ச்சியடைந்தது. 2017-ல் NABH அங்கீகாரம் பெற்றது.",
      stats: {
        beds: "75+ படுக்கைகள்",
        experience: "40+ ஆண்டுகள்",
        specialists: "50+ மருத்துவர்கள்",
        patients: "1.5L+ குடும்பங்கள்",
      },
    },
    // Specialities Section
    specialities: {
      title: "எங்கள் சிறப்புகள்",
      subtitle: "பல துறைகளில் விரிவான பராமரிப்பு",
      departments: {
        obgyn: {
          name: "மகப்பேறு & பேறுகால பராமரிப்பு",
          desc: "40+ ஆண்டுகள் மரபு. சாதாரண & உயர் ஆபத்து கர்ப்பம், மலட்டுத்தன்மை (IUI/IVF), லாப்ராஸ்கோபிக் அறுவை சிகிச்சை, CAPSI புற்றுநோய் பரிசோதனை.",
        },
        cardio: {
          name: "இதயவியல்",
          desc: "மாரடைப்பு சிகிச்சை, இதய செயலிழப்பு, அரிதமியா, ECG, ECHO, TMT மற்றும் ஊடுருவாத இதய நடைமுறைகள்.",
        },
        urology: {
          name: "சிறுநீரகவியல்",
          desc: "லேசர் கல் அகற்றல், லேசர் புரோஸ்டேட் அறுவை சிகிச்சை, மினி PCNL, ரோபோடிக் அறுவை சிகிச்சை, சிறுநீரக மாற்று.",
        },
        ortho: {
          name: "எலும்பியல்",
          desc: "மூட்டு மாற்று, ஆர்த்ரோஸ்கோபி, முதுகெலும்பு அறுவை சிகிச்சை, அதிர்ச்சி எலும்பு முறிவு, விளையாட்டு காயங்கள்.",
        },
        gastro: {
          name: "இரைப்பை குடல் இயல்",
          desc: "எண்டோஸ்கோபி, கொலோனோஸ்கோபி, லாப்ராஸ்கோபிக் அறுவை சிகிச்சைகள், கல்லீரல் மற்றும் கணைய நடைமுறைகள்.",
        },
        ent: {
          name: "காது மூக்கு தொண்டை",
          desc: "டான்சிலெக்டமி, மாஸ்டாய்டெக்டமி, FESS அறுவை சிகிச்சை, தைராய்டு அறுவை சிகிச்சை, குறட்டை சிகிச்சை.",
        },
        paediatrics: {
          name: "குழந்தை நல & நவஜாத சிகிச்சை",
          desc: "வென்டிலேட்டருடன் முழுமையான NICU, நவஜாத சிகிச்சை நிபுணர், தடுப்பூசி சேவைகள்.",
        },
        medicine: {
          name: "பொது & உள் மருத்துவம்",
          desc: "நீரிழிவு மேலாண்மை, காய்ச்சல், தொற்றுகள், தைராய்டு, சுகாதார பரிசோதனைகள், நுரையீரல் மருத்துவம்.",
        },
        spine: {
          name: "முதுகெலும்பு & நரம்பியல்",
          desc: "கிரேனியோட்டமி, ஸ்பாண்டிலோசிஸ், ஸ்கோலியோசிஸ், முதுகெலும்பு கட்டிகள், நழுவிய வட்டு சிகிச்சை.",
        },
        oncology: {
          name: "புற்றுநோயியல்",
          desc: "CAPSI — புற்றுநோய் விழிப்புணர்வு, தடுப்பு & பரிசோதனை முன்முயற்சி. பல்துறை அணுகுமுறை.",
        },
        surgery: {
          name: "லாப்ராஸ்கோபி & பொது அறுவை சிகிச்சை",
          desc: "குறைந்த ஊடுருவல் அறுவை சிகிச்சைகள், குடலிறக்கம் பழுது, குடல்வால் அகற்றல், இரத்தநாள அறுவை சிகிச்சை.",
        },
        plastic: {
          name: "பிளாஸ்டிக் & அழகு அறுவை சிகிச்சை",
          desc: "அழகு அறுவை சிகிச்சை (முகம், மார்பு, உடல்), தீக்காய மேலாண்மை, கை காயங்கள், நீரிழிவு பாதம்.",
        },
        diabetology: {
          name: "நீரிழிவு நிபுணர்",
          desc: "வகை I & II நீரிழிவு, கர்ப்ப நீரிழிவு, உடல் பருமன் சிகிச்சை, நீரிழிவு பாத பராமரிப்பு.",
        },
        skin: {
          name: "தோல் & அழகுசாதன மருத்துவம்",
          desc: "அழகுசாதன சிகிச்சைகள், முடி மறுசீரமைப்பு மற்றும் தோல் கோளாறுகள் உட்பட முழுமையான தோல் நோய் சேவைகள்.",
        },
      },
      learnMore: "மேலும் அறிய",
    },
    // Facilities Section
    facilities: {
      title: "உலகத்தரம் வாய்ந்த வசதிகள்",
      subtitle: "உங்கள் பராமரிப்புக்கான நவீன உள்கட்டமைப்பு",
      items: {
        ot: {
          name: "நவீன அறுவை சிகிச்சை அரங்குகள்",
          desc: "லாமினார் காற்று ஓட்டம், நவீன தொழில்நுட்பம் மற்றும் கடுமையான தொற்று கட்டுப்பாட்டுடன் 3 OT-கள்",
        },
        icu: {
          name: "24/7 ICU & இன்டென்சிவிஸ்ட்",
          desc: "இரவு பகல் கண்காணிப்பு, வென்டிலேட்டர் மற்றும் இதய ஆதரவுடன் தீவிர சிகிச்சைப் பிரிவு",
        },
        emergency: {
          name: "24/7 அவசர & ஆம்புலன்ஸ்",
          desc: "24/7 ஆம்புலன்ஸ் சேவைகளுடன் முழுமையாக பொருத்தப்பட்ட அவசர சேவைகள்",
        },
        nicu: {
          name: "NICU & நவஜாத சிகிச்சை",
          desc: "வென்டிலேட்டருடன் முழுமையான NICU, நவஜாத சிகிச்சை நிபுணர், விரிவான புதிதாகப் பிறந்த பராமரிப்பு",
        },
        dialysis: {
          name: "டயாலிசிஸ் பிரிவு",
          desc: "ICU காப்புடன் நவீன டயாலிசிஸ் மையம்",
        },
        lab: {
          name: "மேம்பட்ட கண்டறிதல்கள்",
          desc: "டிஜிட்டல் X-Ray, ECG, 2D ECHO, அல்ட்ராசவுண்ட், எண்டோஸ்கோபி, TMT, PFT மற்றும் ஆய்வக சேவைகள்",
        },
        pharmacy: {
          name: "24 மணி நேர மருந்தகம்",
          desc: "அனைத்து அத்தியாவசிய மற்றும் சிறப்பு மருந்துகளுடன் இரவு பகல் மருந்தகம்",
        },
        homecare: {
          name: "வீட்டு பராமரிப்பு சேவைகள்",
          desc: "வீட்டு வாசலில் வெளியேற்றத்திற்குப் பிந்தைய பராமரிப்பு, காயம் மேலாண்மை, பிசியோதெரபி",
        },
        insurance: {
          name: "காப்பீடு & TPA பணமில்லா",
          desc: "முக்கிய காப்பீட்டு வழங்குநர்களுடன் பணமில்லா சிகிச்சை மற்றும் TPA எளிமைப்படுத்தல்",
        },
        physio: {
          name: "பிசியோதெரபி & மறுவாழ்வு",
          desc: "அறுவை சிகிச்சைக்குப் பிந்தைய மீட்பு, விளையாட்டு காயங்கள் மற்றும் நாள்பட்ட வலிக்கான விரிவான பிசியோதெரபி",
        },
      },
    },
    // Appointment Form
    appointment: {
      title: "சந்திப்பு முன்பதிவு",
      subtitle: "எங்கள் நிபுணர்களுடன் உங்கள் வருகையை திட்டமிடுங்கள்",
      form: {
        name: "முழு பெயர்",
        namePlaceholder: "உங்கள் பெயரை உள்ளிடவும்",
        phone: "தொலைபேசி எண்",
        phonePlaceholder: "+91 XXXXX XXXXX",
        email: "மின்னஞ்சல் முகவரி",
        emailPlaceholder: "your@email.com",
        date: "விரும்பிய தேதி",
        time: "விரும்பிய நேரம்",
        department: "துறையைத் தேர்ந்தெடுக்கவும்",
        message: "கூடுதல் குறிப்புகள் (விருப்பமானது)",
        messagePlaceholder: "ஏதேனும் குறிப்பிட்ட கவலைகள் அல்லது தேவைகள்...",
        submit: "சமர்ப்பிக்கவும்",
        submitting: "சமர்ப்பிக்கிறது...",
      },
      success: "சந்திப்பு வெற்றிகரமாக முன்பதிவு செய்யப்பட்டது! விரைவில் தொடர்பு கொள்வோம்.",
      error: "சந்திப்பை முன்பதிவு செய்ய முடியவில்லை. மீண்டும் முயற்சிக்கவும்.",
      departments: [
        "மகப்பேறு & பேறுகால பராமரிப்பு",
        "சிறுநீரகவியல்",
        "இதயவியல்",
        "எலும்பியல்",
        "இரைப்பை குடல் இயல்",
        "காது மூக்கு தொண்டை",
        "குழந்தை நல & நவஜாத சிகிச்சை",
        "பொது & உள் மருத்துவம்",
        "முதுகெலும்பு & நரம்பியல்",
        "புற்றுநோயியல்",
        "லாப்ராஸ்கோபி & பொது அறுவை சிகிச்சை",
        "பிளாஸ்டிக் & அழகு அறுவை சிகிச்சை",
        "நீரிழிவு நிபுணர்",
        "தோல் & அழகுசாதன மருத்துவம்",
        "டயாலிசிஸ்",
      ],
    },
    // Testimonials
    testimonials: {
      title: "நோயாளிகள் என்ன சொல்கிறார்கள்",
      subtitle: "Google My Business-ன் உண்மையான மதிப்புரைகள்",
    },
    // Blog
    blog: {
      title: "சமீபத்திய புதுப்பிப்புகள்",
      subtitle: "எங்கள் நிபுணர்களிடமிருந்து ஆரோக்கிய குறிப்புகள் மற்றும் செய்திகள்",
      readMore: "மேலும் அறிய",
    },
    // Contact
    contact: {
      title: "தொடர்பு கொள்ளுங்கள்",
      subtitle: "உங்களுக்கு உதவ நாங்கள் இருக்கிறோம்",
      address: {
        title: "முகவரி",
        line1: "சக்தி மருத்துவமனை & ஆராய்ச்சி மையம்",
        line2: "175, பெரிய தெரு, திருவல்லிக்கேணி",
        line3: "சென்னை - 600005, தமிழ்நாடு",
      },
      phone: {
        title: "தொலைபேசி",
        main: "+91 44 2844 2626",
        emergency: "+91 98402 64444",
      },
      email: {
        title: "மின்னஞ்சல்",
        address: "info@sakthihospitals.com",
      },
      hours: {
        title: "வேலை நேரம்",
        opd: "OPD: காலை 9:00 - இரவு 9:00",
        emergency: "அவசரம்: 24/7",
      },
      form: {
        name: "உங்கள் பெயர்",
        email: "மின்னஞ்சல் முகவரி",
        phone: "தொலைபேசி எண்",
        message: "உங்கள் செய்தி",
        submit: "செய்தி அனுப்பு",
      },
    },
    // Footer
    footer: {
      tagline: "உங்கள் நம்பிக்கையே எங்கள் சக்தி",
      description: "25 ஆண்டுகளுக்கும் மேலாக திருவல்லிக்கேணி, ராயப்பேட்டை & மயிலாப்பூர் சேவை செய்யும் நம்பகமான பல்துறை சிறப்பு மருத்துவமனை.",
      quickLinks: "விரைவு இணைப்புகள்",
      departments: "துறைகள்",
      contact: "தொடர்பு கொள்ளுங்கள்",
      copyright: "© 2025 சக்தி மருத்துவமனை & ஆராய்ச்சி மையம். அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.",
    },
    // FAQ
    faq: {
      title: "அடிக்கடி கேட்கப்படும்",
      subtitle: "மக்கள் என்ன",
      questions: [
        {
          q: "சக்தி மருத்துவமனை என்ன சேவைகளை வழங்குகிறது?",
          a: "நாங்கள் மகப்பேறு, இதயவியல், சிறுநீரகவியல், பொது மருத்துவம், எலும்பியல், இரைப்பை குடல் இயல் மற்றும் 24/7 அவசர பராமரிப்பு உட்பட விரிவான சுகாதார சேவைகளை வழங்குகிறோம்."
        },
        {
          q: "சக்தி மருத்துவமனையில் சந்திப்பை எப்படி முன்பதிவு செய்வது?",
          a: "எங்கள் இணையதளம் மூலம் ஆன்லைனில் முன்பதிவு செய்யலாம், +91 44 2846 1515 என்ற எண்ணில் அழைக்கலாம், அல்லது OPD நேரத்தில் (காலை 9 - இரவு 9) நேரடியாக வரலாம்."
        },
        {
          q: "போக்குவரத்து ஏற்பாடு செய்ய முடியுமா?",
          a: "ஆம், அவசர காலங்களில் 24/7 ஆம்புலன்ஸ் சேவைகளை வழங்குகிறோம். உடனடி உதவிக்கு எங்கள் அவசர உதவி எண்ணை அழைக்கவும்."
        },
        {
          q: "என் சந்திப்புக்கு என்ன கொண்டு வர வேண்டும்?",
          a: "உங்கள் முந்தைய மருத்துவ பதிவுகள், தற்போதைய மருந்துகள், செல்லுபடியாகும் அடையாள ஆவணம் மற்றும் காப்பீட்டு ஆவணங்களைக் கொண்டு வரவும்."
        },
        {
          q: "மருந்து நிரப்புதலை எப்படி கோருவது?",
          a: "வேலை நேரத்தில் எங்கள் மருந்தகத்தை நேரில் சந்திக்கலாம் அல்லது தொலைபேசி மூலம் தொடர்பு கொள்ளலாம்."
        }
      ]
    }
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sakthi-lang') || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('sakthi-lang', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'ta' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t, translations }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
